import { ChatCircleText, CheckCircle, Phone, UserCircle, Warning } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "~/foundation/components/Dialog";

export interface WhatsAppHelpStatus {
  configured: boolean;
  number: string | null;
  templateConfigured: boolean;
  linked: boolean;
  phone: string | null;
}

const code = "mt-1 overflow-x-auto rounded-md bg-bg-tertiary px-2 py-1 font-mono text-small";

/** A number inside a Hebrew (RTL) sentence: isolate it so "+•••1234" keeps its order. */
const ltr = (value: string | null | undefined) => (value ? `\u2066${value}\u2069` : "");

function StatusLine({ ok, icon, children }: { ok: boolean; icon: ReactNode; children: ReactNode }) {
  return (
    <li className="flex items-center gap-2">
      {ok ? (
        <CheckCircle size={16} className="text-[color:var(--success)]" aria-hidden />
      ) : (
        <Warning size={16} className="text-[color:var(--warning)]" aria-hidden />
      )}
      {icon}
      <span>{children}</span>
    </li>
  );
}

/**
 * The WhatsApp help window (Docit Work 05 §10): what the channel does, WhatsApp's 24-hour rule in plain
 * words, where this server and this person stand, how a person links their number (every person, once),
 * and how an administrator connects a WhatsApp Business number through Meta (once per server) — the
 * business verification, the System User token, the phone number id, the app secret, the webhook, and
 * the approved template. Every step names what the product really does.
 */
export function WhatsAppHelp({
  open,
  onOpenChange,
  status,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status?: WhatsAppHelpStatus | null;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("WhatsApp — how it works")}
      description={t(
        "Docit sends you a WhatsApp message when a question waits for you, and you answer with a tap or a reply. Two things must be true: this server is connected to a WhatsApp Business number, and your own number is linked to it.",
      )}
      closeLabel={t("Close")}
      size="lg"
    >
      <div className="flex flex-col gap-5 text-body text-text">
        {status ? (
          <ul className="flex flex-col gap-1 rounded-md border border-divider bg-bg-secondary p-3 text-small">
            <StatusLine ok={status.configured} icon={<Phone size={16} aria-hidden />}>
              {status.configured
                ? status.number
                  ? t("This server sends from the WhatsApp number {{number}}.", {
                      number: ltr(status.number),
                    })
                  : t("This server is connected to WhatsApp.")
                : t(
                    "This server is not connected to WhatsApp yet — see the administrator steps below.",
                  )}
            </StatusLine>
            {status.configured ? (
              <StatusLine
                ok={status.templateConfigured}
                icon={<ChatCircleText size={16} aria-hidden />}
              >
                {status.templateConfigured
                  ? t(
                      "An approved notice template is set up, so questions reach you even after 24 quiet hours.",
                    )
                  : t(
                      "No notice template yet — after 24 quiet hours, questions wait until you write.",
                    )}
              </StatusLine>
            ) : null}
            <StatusLine ok={status.linked} icon={<UserCircle size={16} aria-hidden />}>
              {status.linked
                ? t("Your number {{phone}} is linked.", { phone: ltr(status.phone) })
                : t("Your number is not linked yet.")}
            </StatusLine>
          </ul>
        ) : null}

        <section aria-labelledby="wa-help-window">
          <h3 id="wa-help-window" className="text-h3 text-text">
            {t("The 24-hour rule")}
          </h3>
          <p className="mt-2">
            {t(
              "WhatsApp lets a business write to you freely only during the 24 hours after your last message to it. Inside that window a question arrives as a full card with buttons.",
            )}
          </p>
          <p className="mt-1">
            {t(
              "When the window is closed, Docit sends a short approved notice instead — “A question is waiting for you in Docit: KEY · title”. Reply to it (anything) and the full question follows at once. Without an approved notice, the question waits until you next write.",
            )}
          </p>
        </section>

        <section aria-labelledby="wa-help-link">
          <h3 id="wa-help-link" className="text-h3 text-text">
            {t("Link your WhatsApp — every person, once")}
          </h3>
          <ol className="mt-2 list-decimal space-y-1.5 ps-5">
            <li>
              {t(
                "On the WhatsApp settings page press “Connect WhatsApp”. Docit makes a link that works once, for 15 minutes.",
              )}
            </li>
            <li>
              {t(
                "Open the link on the phone that has your WhatsApp. WhatsApp opens a chat with Docit's number with a message that starts with “link” already typed. Press Send — do not change the text.",
              )}
            </li>
            <li>
              {t(
                "On a computer? The settings page also shows the message; send it from your phone to the number shown.",
              )}
            </li>
            <li>
              {t(
                "Within a few seconds the settings page says “Connected”, with your name and the last digits of your number. From then on, questions arrive in that chat.",
              )}
            </li>
          </ol>
          <p className="mt-2 text-small text-text-secondary">
            {t(
              "In the chat: tap a button or pick from the list to answer, or reply to the question message itself — a choice by its number or name, an approval with yes or no. Send “inbox” to see what is waiting, “help” for the commands; “stop” disconnects every workspace.",
            )}
          </p>
          <p className="mt-1 text-small text-text-tertiary">
            {t(
              "The link binds the number that sends the message, so nobody can connect your number by typing it. Docit shows your number masked everywhere else.",
            )}
          </p>
        </section>

        <section aria-labelledby="wa-help-admin">
          <h3 id="wa-help-admin" className="text-h3 text-text">
            {t("Connect a WhatsApp Business number — administrator, once per server")}
          </h3>
          <p className="mt-2 text-small text-text-secondary">
            {t(
              "Docit talks to Meta's WhatsApp Cloud API directly — no extra provider. Start with step 1: Meta's business verification can take a few days.",
            )}
          </p>
          <ol className="mt-2 list-decimal space-y-1.5 ps-5">
            <li>
              {t(
                "In Meta Business Suite (business.facebook.com) open Settings → Security Center and verify your business.",
              )}
            </li>
            <li>
              {t(
                "At developers.facebook.com create an app of type “Business” and add the WhatsApp product to it. Under WhatsApp → API Setup add the phone number Docit will send from, and confirm it by SMS or call. Use a number dedicated to Docit.",
              )}
            </li>
            <li>
              {t(
                "On the same API Setup page copy the “Phone number ID” — a long id, not the phone number itself.",
              )}
            </li>
            <li>
              {t(
                "Make a permanent token: Business Settings → Users → System users → add an admin system user, assign it the app and the WhatsApp account, then “Generate new token” with the permissions whatsapp_business_messaging and whatsapp_business_management, expiry “Never”. Treat it as a password.",
              )}
            </li>
            <li>{t("In the app's settings (App settings → Basic) copy the App secret.")}</li>
            <li>
              {t("Choose a verify token — any long random string, for example from:")}
              <pre className={code}>openssl rand -hex 24</pre>
            </li>
            <li>
              {t("On the server, put them in the .env file next to docker-compose.production.yml:")}
              <pre className={code}>
                {
                  "WHATSAPP_ACCESS_TOKEN=EAAG…\nWHATSAPP_PHONE_NUMBER_ID=123456789012345\nWHATSAPP_APP_SECRET=…\nWHATSAPP_VERIFY_TOKEN=…\nWHATSAPP_TEMPLATE_NAME=docit_question\nWHATSAPP_TEMPLATE_LANGUAGE=en"
                }
              </pre>
              {t("then redeploy the API and the worker:")}
              <pre className={code}>
                {"./deploy-all.sh --only knowit-web-api\n./deploy-all.sh --only knowit-worker"}
              </pre>
            </li>
            <li>
              {t(
                "Back in the app: WhatsApp → Configuration → Webhook → Edit. Callback URL and verify token:",
              )}
              <pre className={code}>
                {
                  "https://kms.docit.pro/api/work.chat.whatsapp.webhook\n<the WHATSAPP_VERIFY_TOKEN you chose>"
                }
              </pre>
              {t("Press “Verify and save”, then subscribe the webhook field “messages”.")}
            </li>
            <li>
              {t(
                "Subscribe the app to your WhatsApp Business Account, or messages to a real number never reach the webhook. With the WhatsApp Business Account id (WhatsApp → API Setup) and the token:",
              )}
              <pre className={code}>
                {
                  'curl -X POST "https://graph.facebook.com/v23.0/<WABA_ID>/subscribed_apps" \\\n  -H "Authorization: Bearer $WHATSAPP_ACCESS_TOKEN"'
                }
              </pre>
            </li>
            <li>
              {t(
                "Switch the app to Live mode (the toggle at the top of the App Dashboard), and add a payment method to the WhatsApp Business Account (Business Settings → Billing & payments) — Meta refuses paid notice templates without one.",
              )}
            </li>
            <li>
              {t(
                "Recommended — the notice for closed windows: in WhatsApp Manager → Message templates create a template in the category “Utility”, named docit_question, language English, with the body:",
              )}
              <pre className={code}>
                {
                  "A question is waiting for you in Docit: {{1}}. Reply to this message to see it and answer."
                }
              </pre>
              {t(
                "Meta reviews it (minutes to hours). The name and language must match WHATSAPP_TEMPLATE_NAME and WHATSAPP_TEMPLATE_LANGUAGE.",
              )}
            </li>
          </ol>
          <p className="mt-2 text-small text-text-secondary">
            {t(
              "Cost: Meta charges per notice template sent, by country; cards and answers inside the 24-hour window are free. If the token ever leaks, delete it in System users, make a new one, update .env and redeploy.",
            )}
          </p>
          <p className="mt-1 text-small text-text-tertiary">
            {t(
              "Meta's rules forbid general-purpose AI chat assistants on WhatsApp business numbers. Docit uses WhatsApp for questions and answers about your work, which is allowed — do not turn the number into an open chatbot.",
            )}
          </p>
        </section>
      </div>
    </Dialog>
  );
}
